import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

// Auto-detect basename from URL: supports /raport, /raportlive, or root
function detectBasename() {
  if (typeof window === "undefined") return "/";
  const path = window.location.pathname;
  if (path.startsWith("/raportlive")) return "/raportlive";
  if (path.startsWith("/raport")) return "/raport";
  return "/";
}

export const BASENAME = detectBasename();

// API URL: same origin + basename + /api (or use REACT_APP_BACKEND_URL if set)
function detectApiUrl() {
  if (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== "") {
    return `${process.env.REACT_APP_BACKEND_URL}/api`;
  }
  if (typeof window === "undefined") return "/api";
  const prefix = BASENAME === "/" ? "" : BASENAME;
  return `${window.location.origin}${prefix}/api`;
}

export const API = detectApiUrl();

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(null);
  const [username, setUsername] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem("bakery_token");
    const user = localStorage.getItem("bakery_user");
    const r = localStorage.getItem("bakery_role");
    if (stored) {
      setToken(stored);
      setUsername(user);
      setRole(r || "admin");
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    const res = await axios.post(`${API}/auth/login`, { username, password });
    setToken(res.data.token);
    setUsername(res.data.username);
    setRole(res.data.role || "admin");
    localStorage.setItem("bakery_token", res.data.token);
    localStorage.setItem("bakery_user", res.data.username);
    localStorage.setItem("bakery_role", res.data.role || "admin");
    return res.data;
  };

  const logout = () => {
    setToken(null);
    setUsername(null);
    setRole(null);
    localStorage.removeItem("bakery_token");
    localStorage.removeItem("bakery_user");
    localStorage.removeItem("bakery_role");
  };

  const authAxios = axios.create({ baseURL: API });
  authAxios.interceptors.request.use((config) => {
    const t = token || localStorage.getItem("bakery_token");
    if (t) config.headers.Authorization = `Bearer ${t}`;
    return config;
  });
  authAxios.interceptors.response.use(
    (r) => r,
    (err) => {
      if (err?.response?.status === 401) {
        logout();
      }
      return Promise.reject(err);
    }
  );

  const isAdmin = role === "admin";
  const isViewer = role === "viewer";

  return (
    <AuthContext.Provider value={{ token, username, role, isAdmin, isViewer, login, logout, loading, authAxios }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
